<template>
    <q-page class="public-view form">
        <!-- register success form (activated) -->
        <q-card-section v-if="isActivated">
            <h1 class="activated-message">
                <q-icon
                    name="fal fa-check-circle"
                    class="q-ma-sm"
                    size="md"
                    color="green"
                ></q-icon>
                {{ $t('Your account is active now') }}!
            </h1>
            <!-- Form Body-->
            <q-form
                class="q-pl-xl q-pr-xl q-pb-xl"
                :class="$q.platform.is.ipad ? 'ios-form' : 'q-pt-md q-px-none'"
                v-if="isActivated"
            >
                <q-btn
                    class="q-mt-md"
                    @click="$router.push({ name: 'login' })"
                    :label="$t('Return to login')"
                    style="width: 100%"
                    text-color="primary"
                    color="white"
                    outline
                />
            </q-form>
        </q-card-section>
        <!-- register form  (deactivated) -->
        <q-card-section v-else>
            <q-banner
                id="error-message"
                v-for="(errorMsg, index) in errorMessages"
                :key="`error-${index}`"
                class="background-red border-red q-mb-md q-mr-sm"
                rounded
                inline-actions
            >
                <template v-slot:avatar>
                    <q-icon
                        name="fa-light fa-circle-exclamation"
                        color="negative"
                        class="q-pt-sm"
                        size="sm"
                    />
                </template>
                <span class="text-negative">{{ $t(errorMsg) }}</span>
            </q-banner>
            <p class="register-message">
                {{ $t('Welcome to ioTORQ LEAN') }}!<br />
                {{ $t('To activate your account, please select a password.') }}
            </p>
            <!-- Form Body -->
            <q-form
                @submit="handleSubmit"
                ref="registerForm"
                class="q-px-md"
                :class="$q.platform.is.ipad ? 'ios-form' : 'q-pt-md q-px-md'"
            >
                <password-and-confirmation @inputUpdate="passwordUpdate" />

                <q-btn
                    id="submit-button"
                    :label="$t('Submit')"
                    style="width: 100%"
                    type="submit"
                    class="q-my-md"
                    color="primary"
                    :disabled="disableSubmit"
                />
            </q-form>
        </q-card-section>
    </q-page>
</template>

<script>
import PasswordAndConfirmation from '@components/PasswordAndConfirmation.vue';
import { mapActions } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'Register',
    components: { PasswordAndConfirmation },

    data() {
        return {
            form: {
                newPassword: '',
                confirmPassword: ''
            },
            uid: null,
            token: null,
            isLoading: false,
            showPassword: false,
            isActivated: false,
            disableSubmit: true,
            // Array of error messages to be displayed in the banner
            errorMessages: []
        };
    },
    computed: {
        /**
         * Server formatted payload.
         * @return {Object} - Server formatted payload of the form.
         */
        payload() {
            // prepare the payload
            const payload = {
                uid: this.uid,
                token: this.token,
                new_password: this.form.newPassword
            };
            return payload;
        }
    },
    created() {
        // set the uid and token from route
        this.uid = this.$route.params['uid'];
        this.token = this.$route.params['token'];
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useAuthStore, ['activateUserAction']),
        /**
         * Handles activation submission.
         */
        async handleSubmit() {
            // begin loading
            this.beginLoading(`${this.$t('Sending activation request')}...`);

            try {
                const response = await this.activateUserAction(this.payload);

                // if status code is 204, the account is successfully activated
                if (response.status === 204) {
                    this.$notifySuccess(this.$t('Your account is active now'));
                    this.isActivated = true;
                } else if (response.status === 403) {
                    // if the status code is 403, the account is already activated
                    this.$notifyError(
                        this.$t('Your account is already active')
                    );
                } else {
                    // notify other issues
                    this.$notifyError(
                        this.$t(
                            'Unable to activate your user account. Please try again later.'
                        )
                    );
                }
            } catch (e) {
                const error =
                    e?.response?.data && typeof e.response.data === 'object'
                        ? e.response.data
                        : {};
                // notify the password issues
                if (error['new_password']) {
                    // Clear the error messages array
                    this.errorMessages = [];
                    error['new_password'].forEach(errorMsg => {
                        // Push the error message to the array
                        this.errorMessages.push(errorMsg);
                    });
                    this.$notifyError(
                        this.$t(
                            'Please review the errors below and try to set your password again.'
                        )
                    );
                } else if (error['uid']) {
                    // this error key is used when token in url does not correspond to a user account in the database
                    this.$notifyError(
                        this.$t(
                            "This e-mail link is not valid. Please contact support{'@'}iotorq-lean.com to activate your account."
                        )
                    );
                    // log error
                    error['uid'].forEach(errorMsg => {
                        console.error('errorMsg: ', errorMsg);
                    });
                } else if (error['token']) {
                    // notify token issues
                    this.$notifyError(
                        this.$t(
                            "This e-mail link invitation has expired. Please contact support{'@'}iotorq-lean.com to activate your account."
                        )
                    );
                    // log error
                    error['token'].forEach(errorMsg => {
                        console.error('errorMsg: ', errorMsg);
                    });
                } else {
                    // notify other issues
                    this.$notifyError(
                        this.$t(
                            'Unable to activate your user account. Please try again later.'
                        ),
                        error
                    );
                }
            } finally {
                // stop loading
                this.stopLoading();
            }
        },
        passwordUpdate(passwordObj) {
            this.form.newPassword = passwordObj.new;
            this.form.confirmPassword = passwordObj.confirmation;
            this.disableSubmit = !passwordObj.isConfirmed;
        }
    }
};
</script>

<style lang="scss" scoped>
@import '@css/auth_forms.scss';
.form {
    width: 400px;
}

.register-message {
    font-size: 16px;
    margin-top: 15px;
    margin-bottom: 15px;
    text-align: center;
}
.activated-message {
    font-size: 20px;
    margin-top: 50px;
    text-align: center;
}
</style>
